import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { wrapPromise } from "go-core";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { partnerApi } from "../../../../../../../../../../services/Api/Partner/partnerApi";
import { LoanApi, OrganizationApi } from "../../../../../../../../../../services/Api/types";
import { selectOrganization } from "../../../../../../../../../../services/organizations/selectors";
import { selectPartner } from "../../../../../../../../../../services/partner/selectors";
import CreateLoanModal from "../../../../../../../../components/Loan/CreateLoanModal";
import { FormatLoanStatuses } from "../../../../components/FormatLoanStatus";

interface ListState {
	resource?: Record<string, any>;
	partnerId: number;
}

const listName = "LOAN";
const resourceType = "LOAN";

const List = ({ resource, partnerId }: ListState) => {
	const [items, setItems] = useState<LoanApi[]>([]);
	const [params, setParams] = useState<Record<string, any>>({});
	const [showLoanModal, setShowLoanModal] = useState(false);
	const partner = useSelector(selectPartner);
	const { t } = useTranslation("admin");
	const segmentContextValue = React.useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;
	const organization = useSelector(selectOrganization);

	if (!resource) return null;
	const data = resource.read();

	const onHideModal = async (data?: OrganizationApi) => {
		setShowLoanModal(false);
		if (data) {
			if (config.fetch) {
				const res = await config.fetch(params);
				setItems(res.data);
			}
		}
	};

	let config = {
		selectedSorts: ["-created_at"],
		fields: [
			{
				id: "id",
				name: t("modules.loan.field.loan_id.title"),
				type: "text",
				render: (item: LoanApi) => {
					return (
						<div className="d-flex organization-name">
							<div className="d-flex flex-column">
								<Link to={`/partnership/${item.partner.id}/loans/${item.id}`}>{item.reference_id}</Link>
								<small>{item.id}</small>
							</div>
						</div>
					);
				},
				renderExport: (item: LoanApi) => item.reference_id,
			},
			{
				id: "organization",
				name: t("lib:common.word.location"),
				render: (item: LoanApi) => (
					<Link
						target={"_blank"}
						to={`/partnership/${item.partner.id}/organizations/${item.organization?.id}`}
					>
						{item.organization?.name}
					</Link>
				),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						partnerApi.getPartnerOrganizationsSearchSelect(
							search,
							params,
							{
								cancelToken: options?.token,
							},
							partnerId
						),
				},
			},
			{
				id: "start_at",
				name: t("modules.loan.field.start_at.title"),
				type: "date",
				render: (item: LoanApi) => (
					<Link to={`${location.pathname}/${item.id}`}>{FormatDate(item.start_at).split(",")[0]}</Link>
				),
				renderExport: (item: LoanApi) => FormatDate(item.start_at).split(",")[0],
			},
			{
				id: "created_at",
				name: t("lib:common.word.created_at"),
				type: "date",
				render: (item: LoanApi) => (
					<Link to={`${location.pathname}/${item.id}`}>{FormatDate(item.created_at)}</Link>
				),
				renderExport: (item: LoanApi) => FormatDate(item.created_at),
			},
			{
				id: "created_by",
				name: t("modules.loan.field.created_by.title"),
			},
			{
				id: "items",
				name: t("modules.loan.field.devices.title"),
				disableSorting: true,
				render: (item: LoanApi) => {
					if (item.items.length > 0) {
						return (
							<div className={"d-flex flex-column"}>
								{item.items.map((it, index) => {
									return (
										<div key={index}>
											{it.name}|{it.serial_id}
										</div>
									);
								})}
							</div>
						);
					}
					return null;
				},
				renderExport: (item: LoanApi) => {
					return item.items.map((it) => `${it.name}|${it.serial_id}`).join(", ");
				},
			},
			{
				id: "type",
				name: t("modules.loan.field.loan_type.title"),
				type: "list",
				options: {
					GOPOS: "GoPOS",
					POSNET: "Posnet",
				},
			},
			{
				id: "status",
				name: t("common.word.status", { ns: "lib" }),
				type: "list",
				options: {
					DRAFT: t("enums.common.status.DRAFT"),
					OPEN: t("enums.common.status.OPEN"),
					CLOSED: t("enums.common.status.CLOSED"),
					ACCEPTED: t("enums.common.status.ACCEPTED"),
					CANCELLATION_PENDING: t("enums.common.status.CANCELLATION_PENDING"),
					CANCELLED: t("enums.common.status.CANCELLED"),
					DELETED: t("enums.common.status.DELETED"),
				},
				render: (item: LoanApi) => {
					const style = {
						width: "fit-content",
						"margin-bottom": "4px",
					};
					return (
						<div className="d-flex flex-column align-items-center">
							<FormatLoanStatuses loan={item} loanStatusStyle={style} partnerStatusStyle={style} />
						</div>
					);
				},
				renderExport: (item: LoanApi) => t(`enums.common.status.${item.status}`),
			},
			{
				id: "cancelled_at",
				name: t("modules.loan.field.cancelled_at.title"),
				type: "date",
				render: (item: LoanApi) => FormatDate(item.cancelled_at),
			},
			{
				id: "cancellation_reason",
				name: t("modules.loan.field.cancellation_reason.title"),
				type: "text",
			},
		],
		filters: [
			{
				id: "serial_id",
				name: t("modules.loan.field.serial_id.title"),
				type: "text",
			},
			{
				id: "opened_at",
				name: t("modules.loan.field.opened_at.title"),
				type: "date",
			},
			{
				id: "reference_id",
				name: t("common.word.reference_id"),
				type: "text",
			},
		],
		externalSelectedFilters: [
			{
				id: "partner",
				filterType: "search_select",
				value: partnerId.toString(),
				condition: "e",
				visible: false,
			},
		],
		actions: [
			{
				name: t("common.action.preview", { ns: "lib" }),
				link: (item: LoanApi) => `loans/${item.id}`,
			},
		],
		selectedColumns: ["id", "organization", "status", "start_at", "created_by", "created_at", "items"],
		segments: [
			{
				id: "all",
				slug: "all",
				name: t("common.word.all", { ns: "lib" }),
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "organization,items,partner";
			setParams(params);
			return partnerApi.getLoans(Number(partner?.id), params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => segmentContextValue.save(listName, resourceType, segment),
		exportConfig: {
			title: t("modules.loan.header.title"),
			filename: t("modules.loan.field.export_config.filename"),
			organization: `${organization.name}`,
		},
		numberOfStickyColumnsAtTheStart: 2,
		doesIdColumnRedirectToPreviewPage: true,
		doNotUseDefaultDateRangeInFilters: true,
	} as ListConfig;
	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
	};

	const buttons: ButtonProps[] = [
		{
			title: t("modules.loan.action.rent.title"),
			action: () => setShowLoanModal(true),
			variant: "primary",
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("modules.loan.action.rent.title"),
			action: () => setShowLoanModal(true),
		},
	];

	return (
		<>
			{!isMobile && <Header title={t("modules.loan.header.title")} buttons={buttons} />}
			{showLoanModal && <CreateLoanModal partnerId={partnerId} onHide={onHideModal} />}
			<ListData
				data={items}
				config={config}
				onFetch={(items: LoanApi[]) => setItems(items)}
				mobileActions={mobileActions}
			/>
		</>
	);
};

const PartnerPreviewLoansIndexPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = React.useContext(SegmentContext);
	const partnerId = useSelector(selectPartner)?.id;
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { t } = useTranslation("admin");

	useEffect(() => {
		handleChangeTabTitle(`${t("modules.loan.header.title")} | ${t("modules.partner.field.partner_panel.title")}`);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<List partnerId={Number(partnerId)} resource={resource} />
		</Suspense>
	);
};
export default PartnerPreviewLoansIndexPage;
